import { useEffect } from 'react';
import { ScriptGenerationStatus } from '@packages/global';
import { useWebSocketContext } from '../../services/websocket/WebSocketContext';
import { MessageType } from '../../services/websocket/types';
import type {
  ScriptStepProgressMessage,
  ScriptStatusChangedMessage,
  ScriptStep1StorylineMessage,
  EpisodeTitleUpdatedMessage,
  ScriptShotsGeneratedMessage
} from '../../services/websocket/types';
import type { Step1Progress, ShotsGeneratingProgress } from './useScriptPageState';

interface UseScriptWebSocketOptions {
  scriptId: string | undefined;
  episodeId: string | undefined;
  setIsGenerating: (v: boolean) => void;
  setIsStep1Generating: (v: boolean) => void;
  setStep1Progress: (fn: (prev: Step1Progress) => Step1Progress) => void;
  setIsFailed: (v: boolean) => void;
  setErrorMessage: (v: string | undefined) => void;
  setShotsGeneratingProgress: (fn: (prev: ShotsGeneratingProgress) => ShotsGeneratingProgress) => void;
  setNewShotIndices: (fn: (prev: number[]) => number[]) => void;
  markStepInProgress: (step: number) => void;
  markStepComplete: (step: number) => void;
  refreshScript: () => Promise<void>;
  refreshEpisode: () => void;
  refreshEpisodeAssets?: () => Promise<void>;
}

export function useScriptWebSocket({
  scriptId, episodeId,
  setIsGenerating, setIsStep1Generating, setStep1Progress,
  setIsFailed, setErrorMessage,
  setShotsGeneratingProgress, setNewShotIndices,
  markStepInProgress, markStepComplete,
  refreshScript, refreshEpisode, refreshEpisodeAssets,
}: UseScriptWebSocketOptions) {
  const { client: wsClient, isConnected: wsConnected } = useWebSocketContext();

  useEffect(() => {
    if (!wsClient || !wsConnected || !scriptId || !episodeId) return;

    // 注意：episode 房间订阅由 WithEpisodeSubscription / EpisodeSubscriptionProvider 统一管理，
    // 此处只注册消息监听器，不重复订阅。

    const unsubStatus = wsClient.onMessage(MessageType.SCRIPT_STATUS_CHANGED, (msg: ScriptStatusChangedMessage) => {
      if (msg.scriptId !== scriptId) return;
      if (msg.newStatus === ScriptGenerationStatus.PROCESSING) setIsStep1Generating(true);
      if (msg.newStatus === ScriptGenerationStatus.COMPLETED || msg.newStatus === ScriptGenerationStatus.FAILED) {
        setIsStep1Generating(false);
        refreshScript();
      }
      if (msg.newStatus === ScriptGenerationStatus.FAILED) {
        setIsFailed(true);
        setErrorMessage(msg.errorMessage || '生成失败');
      }
    });

    const unsubProgress = wsClient.onMessage(MessageType.SCRIPT_STEP_PROGRESS, (msg: ScriptStepProgressMessage) => {
      if (msg.scriptId !== scriptId) return;
      if (msg.status === 'processing') {
        markStepInProgress(msg.stepNumber);
        setIsGenerating(true);
        // 数据有更新（列表或图片），刷新脚本数据
        refreshScript();
        // Step 3/4/5：主体数据以 ProjectAsset 为主数据源，进度推进时同步刷新 episodeAssets
        // 这样在自动化流程中新建的角色/道具/场景能立即出现在前端，且 Step 6 分镜卡片能取到图片
        if (msg.stepNumber >= 3 && msg.stepNumber <= 6) {
          refreshEpisodeAssets?.();
        }
      } else if (msg.status === 'completed') {
        markStepComplete(msg.stepNumber);
        setIsGenerating(false);
        refreshScript();
        if (msg.stepNumber >= 3 && msg.stepNumber <= 6) {
          refreshEpisodeAssets?.();
        }
      } else if (msg.status === 'failed') {
        setIsGenerating(false);
        setIsFailed(true);
      }
      if (msg.stepNumber === 1) {
        if (msg.status === 'completed') { setIsStep1Generating(false); refreshEpisode(); }
        else if (msg.status === 'processing') setIsStep1Generating(true);
      }
    });

    const unsubStep1 = wsClient.onMessage(MessageType.SCRIPT_STEP1_STORYLINE, (msg: ScriptStep1StorylineMessage) => {
      if (msg.scriptId !== scriptId) return;
      if (msg.field && msg.fullContent !== undefined) {
        setStep1Progress(prev => ({
          ...prev,
          field: msg.field,
          content: msg.fullContent,
          ...(msg.field === 'title' && { title: msg.fullContent }),
          ...(msg.field === 'welcomeMessage' && { welcomeMessage: msg.fullContent }),
          ...(msg.field === 'summary' && { summary: msg.fullContent }),
          isComplete: msg.isComplete,
        }));
      }
      if (msg.isComplete) {
        setIsStep1Generating(false);
      } else {
        setIsStep1Generating(true);
      }
    });

    const unsubEpisodeTitle = wsClient.onMessage(MessageType.EPISODE_TITLE_UPDATED, (msg: EpisodeTitleUpdatedMessage) => {
      if (msg.episodeId !== episodeId) return;
      refreshEpisode();
    });

    const unsubShots = wsClient.onMessage(MessageType.SCRIPT_SHOTS_GENERATED, (msg: ScriptShotsGeneratedMessage) => {
      if (msg.scriptId !== scriptId) return;
      const currentCount = msg.startIndex + (msg.newShots?.length || 0);
      setShotsGeneratingProgress(() => ({
        current: currentCount,
        total: msg.totalShotCount,
        isGenerating: !msg.isComplete
      }));
      if (msg.newShots?.length) {
        const indices = msg.newShots.map((_, i) => msg.startIndex + i);
        setNewShotIndices(prev => [...prev, ...indices]);
      }
      refreshScript();
      // 分镜实时推送时刷新 episodeAssets，让分镜卡片中的角色/道具/场景缩略图及时呈现
      refreshEpisodeAssets?.();
      if (msg.isComplete) setShotsGeneratingProgress(() => ({ current: 0, total: 0, isGenerating: false }));
    });

    return () => {
      unsubStatus(); unsubProgress(); unsubStep1(); unsubEpisodeTitle(); unsubShots();
      // 注意：不在此处调用 unsubscribeEpisode，episode 房间订阅由 EpisodeSubscriptionProvider 统一管理。
      // 之前此处调用 unsubscribeEpisode 会导致 Provider 的订阅被意外取消，
      // 触发 Provider 重新订阅 → useEffect 重跑 → 再次取消的乒乓循环。
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsClient, wsConnected, scriptId, episodeId]);
}
